import { useContext, useEffect, useState, useMemo } from "react";
import { BreadTransaction, FirestoreDocCategory } from "breadcommon";
import { BreadBudget } from "breadcommon";
import { buildClasses } from "../utils/buildClasses";
import { CategoryGroupsContext } from "../firebaseio/CategoryGroupsContext";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { remeasureAfterTransitions } from "../rye/remeasureAfterTransitions";
import { DollarAmount } from "../common/DollarAmount";
import { TransactionsContext } from "../firebaseio/TransactionsContext";
import { useExchangeRates } from "../firebaseio/ExchangeRatesContext";
import { RyeIcon } from "../rye/RyeIcon";
import { useGetGroupedCategoriesForBudget } from "./useGetGroupedCategoriesForBudget";
import { getSignedBudgetLineItemAmount } from "../budgets_screen/budgetUtils";
import {
  SelectedTransactionsContext,
  CellSelectionContext,
  ColumnVisibility,
} from "./BudgetTrackingReport";
import { RyeButton } from "../rye/RyeButton";
import {
  getFractionOfTimeIntoBudget,
  getTransactionsToDate,
  getMonthlyTransactions,
  getMonthlyTotals,
  calculateMonthly,
  sumTransactions,
} from "./budgetTrackingUtils";

const CELL_HEIGHT = 2.25; // rem

// Helper function to calculate freeze indices based on visible columns
function getFreezeIndex(
  columnVisibility: ColumnVisibility,
  baseIndex: number,
): number {
  let adjustedIndex = 0;
  if (baseIndex === 0) return 0; // Category column is always first

  if (baseIndex === 1 && columnVisibility.totalBudgeted) adjustedIndex = 1;
  else if (baseIndex === 2 && columnVisibility.budgetedToDate) {
    adjustedIndex = (columnVisibility.totalBudgeted ? 1 : 0) + 1;
  } else if (baseIndex === 3 && columnVisibility.actualsToDate) {
    adjustedIndex =
      (columnVisibility.totalBudgeted ? 1 : 0) +
      (columnVisibility.budgetedToDate ? 1 : 0) +
      1;
  } else return -1; // Column not visible

  return adjustedIndex;
}

// Helper to determine which column is the rightmost frozen regardless of scroll, for persistent border
function isRightmostFrozen(
  columnVisibility: ColumnVisibility,
  baseIndex: number,
): boolean {
  // Find the rightmost visible frozen column
  let rightmostFrozenColumn = 0; // Category is always visible
  if (columnVisibility.actualsToDate) rightmostFrozenColumn = 3;
  else if (columnVisibility.budgetedToDate) rightmostFrozenColumn = 2;
  else if (columnVisibility.totalBudgeted) rightmostFrozenColumn = 1;

  return baseIndex === rightmostFrozenColumn && columnVisibility.monthlyActuals;
}

// Helper function to determine if a column should show the right shadow
function shouldShowRightShadow(
  columnVisibility: ColumnVisibility,
  baseIndex: number,
  isScrolledX: boolean,
): boolean {
  return isScrolledX && isRightmostFrozen(columnVisibility, baseIndex);
}

export function BudgetTrackingTable(props: {
  budget: BreadBudget;
  columnVisibility: ColumnVisibility;
}): JSX.Element {
  const [groupIsExpanded, setGroupIsExpanded] = useState<Map<string, boolean>>(
    new Map(),
  );
  const [isScrolledY, setIsScrolledY] = useState(false);
  const [isScrolledX, setIsScrolledX] = useState(false);
  const groupedCategories = useGetGroupedCategoriesForBudget();
  const [selectedTransactions] = useContext(SelectedTransactionsContext);
  const [, setSelectedCellId] = useContext(CellSelectionContext);
  const transactions = useContext(TransactionsContext);
  const { convertTransactionsToSpecificCurrency } = useExchangeRates();

  const convertedTransactions = useMemo(() => {
    return convertTransactionsToSpecificCurrency(
      Array.from(transactions.values()),
      props.budget.currency,
    );
  }, [transactions, props.budget.currency, convertTransactionsToSpecificCurrency]);

  useEffect(() => {
    if (selectedTransactions === null) {
      setSelectedCellId(null);
    }
  }, [selectedTransactions, setSelectedCellId]);

  function anyGroupsAreExpanded(): boolean {
    return Array.from(groupIsExpanded.values()).filter((v) => v).length > 0;
  }

  function expandOrCollapseAllGroups() {
    if (anyGroupsAreExpanded()) {
      setGroupIsExpanded((prevState) => {
        const newState = new Map(prevState);
        Array.from(prevState.keys()).forEach((groupId) =>
          newState.set(groupId, false),
        );
        return newState;
      });
    } else {
      setGroupIsExpanded((prevState) => {
        const newState = new Map(prevState);
        Array.from(prevState.keys()).forEach((groupId) =>
          newState.set(groupId, true),
        );
        return newState;
      });
    }
  }

  function setIndividualGroupIsExpanded(
    groupId: string,
    isExpanded: boolean,
  ): void {
    setGroupIsExpanded((prevState) => {
      const newState = new Map(prevState);
      newState.set(groupId, isExpanded);
      return newState;
    });
  }

  const scrollHandler = (_instance: any, event: Event) => {
    const target = event.target as HTMLDivElement;
    if (target.scrollTop === 0) {
      setIsScrolledY(false);
    } else {
      setIsScrolledY(true);
    }
    if (target.scrollLeft === 0) {
      setIsScrolledX(false);
    } else {
      setIsScrolledX(true);
    }
  };

  const rowGroups: JSX.Element[] = [];
  groupedCategories.forEach((categories, groupId) => {
    if (categories.size > 0) {
      rowGroups.push(
        <RowGroup
          key={`group_${groupId}`}
          groupId={groupId}
          categories={categories}
          budget={props.budget}
          isExpanded={groupIsExpanded.get(groupId) ?? false}
          isScrolledX={isScrolledX}
          setGroupIsExpanded={setIndividualGroupIsExpanded}
          columnVisibility={props.columnVisibility}
          convertedTransactions={convertedTransactions}
        />,
      );
    }
  });

  return (
    <div
      className={buildClasses(
        "border",
        "border-surface-500",
        "rounded-lg",
        "w-full",
        "min-h-32",
        "overflow-hidden", // hide non-rounded corners that overflow rounded chart border
      )}
    >
      <OverlayScrollbarsComponent
        options={{
          scrollbars: { autoHide: "move" },
        }}
        className={buildClasses("h-full")}
        events={{
          initialized: remeasureAfterTransitions,
          scroll: scrollHandler,
        }}
      >
        <HeaderRow
          budget={props.budget}
          anyGroupsAreExpanded={anyGroupsAreExpanded()}
          isScrolledY={isScrolledY}
          isScrolledX={isScrolledX}
          expandOrCollapseAllGroups={expandOrCollapseAllGroups}
          columnVisibility={props.columnVisibility}
        />
        <UnreviewedRow
          budget={props.budget}
          isScrolledX={isScrolledX}
          columnVisibility={props.columnVisibility}
          convertedTransactions={convertedTransactions}
        />
        {rowGroups}
      </OverlayScrollbarsComponent>
    </div>
  );
}

function HeaderRow(props: {
  budget: BreadBudget;
  anyGroupsAreExpanded: boolean;
  isScrolledY: boolean;
  isScrolledX: boolean;
  expandOrCollapseAllGroups: () => void;
  columnVisibility: ColumnVisibility;
}) {
  return (
    <div
      className={buildClasses(
        { if: props.isScrolledY, then: buildClasses("clipped-shadow-bottom") },
        "min-w-full",
        "inline-flex", // displaying inline makes width the width work best (fits width of the contents instead of stopping at width of parent)
        "sticky",
        "top-0",
        "bg-surface",
        "z-20",
        "rounded-t-lg",
        "border-b",
        "border-surface-600",
        "font-medium",
      )}
    >
      <Cell
        width="lg"
        freezeIndex={0}
        align={"left"}
        bgColor="header"
        showRightBorder={isRightmostFrozen(props.columnVisibility, 0)}
        cellId="header-category"
      >
        <div
          className={buildClasses(
            "w-full",
            "flex",
            "items-center",
            "gap-1.5",
            "cursor-pointer",
          )}
          onClick={props.expandOrCollapseAllGroups}
        >
          {/* As wide as a group's chevron, so "Category" lines up with the
              group names below it. */}
          <span
            className={buildClasses(
              "w-[1.1rem]",
              "shrink-0",
              "flex",
              "justify-center",
            )}
          >
            <RyeIcon
              name={props.anyGroupsAreExpanded ? "collapse_all" : "expand_all"}
              size="xs"
              className={buildClasses("select-none")}
            />
          </span>
          Category
        </div>
      </Cell>
      {props.columnVisibility.totalBudgeted && (
        <Cell
          width="md"
          freezeIndex={getFreezeIndex(props.columnVisibility, 1)}
          bgColor="header"
          showRightBorder={isRightmostFrozen(props.columnVisibility, 1)}
          cellId="header-total-budgeted"
        >
          Total Budgeted
        </Cell>
      )}
      {props.columnVisibility.budgetedToDate && (
        <Cell
          width="md"
          freezeIndex={getFreezeIndex(props.columnVisibility, 2)}
          bgColor="header"
          showRightBorder={isRightmostFrozen(props.columnVisibility, 2)}
          cellId="header-budgeted-to-date"
        >
          Budgeted to Date
        </Cell>
      )}
      {props.columnVisibility.actualsToDate && (
        <Cell
          width="md"
          freezeIndex={getFreezeIndex(props.columnVisibility, 3)}
          bgColor="header"
          showRightShadow={shouldShowRightShadow(
            props.columnVisibility,
            3,
            props.isScrolledX,
          )}
          showRightBorder={isRightmostFrozen(props.columnVisibility, 3)}
          cellId="header-actuals-to-date"
        >
          Actuals to Date
        </Cell>
      )}
      {props.columnVisibility.monthlyActuals &&
        calculateMonthly(props.budget, (startOfMonth) =>
          startOfMonth.format("MMM 'YY"),
        ).map((monthHeader, i) => (
          <Cell
            key={`table_header_month_${i}`}
            bgColor="header"
            cellId={`header-month-${i}`}
          >
            {monthHeader}
          </Cell>
        ))}
    </div>
  );
}

function RowGroup(props: {
  groupId: string;
  categories: Map<string, FirestoreDocCategory>;
  budget: BreadBudget;
  isExpanded: boolean;
  isScrolledX: boolean;
  setGroupIsExpanded: (groupId: string, isExpanded: boolean) => void;
  columnVisibility: ColumnVisibility;
  convertedTransactions: BreadTransaction[];
}) {
  useEffect(
    () => props.setGroupIsExpanded(props.groupId, props.isExpanded),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  function getHeight(): string {
    if (props.isExpanded) {
      return `${props.categories.size * CELL_HEIGHT}rem`;
    }
    return "0px";
  }

  return (
    <div
      className={buildClasses(
        "inline-flex",
        "flex-col",
        "border-b-tiny",
        "border-surface-600",
        "last:border-none",
        "min-w-full",
      )}
    >
      <GroupHeaderRow
        groupId={props.groupId}
        categories={props.categories}
        budget={props.budget}
        isExpanded={props.isExpanded}
        isScrolledX={props.isScrolledX}
        setGroupIsExpanded={props.setGroupIsExpanded}
        columnVisibility={props.columnVisibility}
        convertedTransactions={props.convertedTransactions}
      />
      <div
        className={buildClasses(
          "transition-height",
          "min-w-full",
          "inline-block",
          "overflow-clip",
          "duration-300",
        )}
        style={{ height: getHeight() }}
      >
        {Array.from(props.categories.values()).map((category) => (
          <GroupMemberRow
            key={`group_member_${category.id}`}
            category={category}
            budget={props.budget}
            isScrolledX={props.isScrolledX}
            columnVisibility={props.columnVisibility}
            convertedTransactions={props.convertedTransactions}
          />
        ))}
      </div>
    </div>
  );
}

function GroupHeaderRow(props: {
  groupId: string;
  categories: Map<string, FirestoreDocCategory>;
  budget: BreadBudget;
  isExpanded: boolean;
  isScrolledX: boolean;
  setGroupIsExpanded: (groupId: string, isExpanded: boolean) => void;
  columnVisibility: ColumnVisibility;
  convertedTransactions: BreadTransaction[];
}) {
  const categoryGroups = useContext(CategoryGroupsContext);
  const group = categoryGroups.get(props.groupId)!;
  const transactionsForGroup = props.convertedTransactions.filter(
    (transaction) =>
      transaction.reviewed &&
      transaction.categoryId !== null &&
      props.categories.has(transaction.categoryId),
  );

  function getTotalBudgeted() {
    return Array.from(props.categories).reduce(
      (acc, [categoryId, category]) => {
        if (props.budget.line_items.has(categoryId)) {
          const lineItem = props.budget.line_items.get(categoryId);
          return (acc += getSignedBudgetLineItemAmount(lineItem, category));
        }
        return acc;
      },
      0,
    );
  }

  function getBudgetedToDate() {
    return getTotalBudgeted() * getFractionOfTimeIntoBudget(props.budget);
  }

  const transactionsToDate: BreadTransaction[] = getTransactionsToDate(
    transactionsForGroup,
    props.budget,
  );
  const totalToDate = sumTransactions(transactionsToDate);
  const monthlyTransactions: BreadTransaction[][] = getMonthlyTransactions(
    transactionsForGroup,
    props.budget,
  );
  const monthlyTotals = getMonthlyTotals(monthlyTransactions);

  return (
    <div className={buildClasses("inline-flex", "min-w-full", "font-normal")}>
      <Cell
        width="lg"
        freezeIndex={0}
        align={"left"}
        bgColor="subheader"
        showRightBorder={isRightmostFrozen(props.columnVisibility, 0)}
        cellId={`group-${props.groupId}-name`}
      >
        <div
          className={buildClasses(
            "w-full",
            "flex",
            "items-center",
            "gap-1.5",
            "cursor-pointer",
          )}
          onClick={() =>
            props.setGroupIsExpanded(props.groupId, !props.isExpanded)
          }
        >
          <RyeIcon
            name="keyboard_arrow_right"
            className={buildClasses(
              { if: props.isExpanded, then: "rotate-90" },
              "shrink-0",
              "select-none",
              "transition-transform",
            )}
            size="sm"
          />
          <div
            className={buildClasses(
              "w-full",
              "overflow-hidden",
              "whitespace-nowrap",
              "text-ellipsis",
            )}
          >
            {group.name}
          </div>
        </div>
      </Cell>
      {props.columnVisibility.totalBudgeted && (
        <Cell
          width="md"
          freezeIndex={getFreezeIndex(props.columnVisibility, 1)}
          isDollarAmount
          bgColor="subheader"
          showRightBorder={isRightmostFrozen(props.columnVisibility, 1)}
          cellId={`group-${props.groupId}-total-budgeted`}
          currencyCode={props.budget.currency}
        >
          {getTotalBudgeted()}
        </Cell>
      )}
      {props.columnVisibility.budgetedToDate && (
        <Cell
          width="md"
          freezeIndex={getFreezeIndex(props.columnVisibility, 2)}
          isDollarAmount
          bgColor="subheader"
          showRightBorder={isRightmostFrozen(props.columnVisibility, 2)}
          cellId={`group-${props.groupId}-budgeted-to-date`}
          currencyCode={props.budget.currency}
        >
          {getBudgetedToDate()}
        </Cell>
      )}
      {props.columnVisibility.actualsToDate && (
        <Cell
          width="md"
          freezeIndex={getFreezeIndex(props.columnVisibility, 3)}
          isDollarAmount
          bgColor="subheader"
          showRightShadow={shouldShowRightShadow(
            props.columnVisibility,
            3,
            props.isScrolledX,
          )}
          showRightBorder={isRightmostFrozen(props.columnVisibility, 3)}
          backingTransactions={transactionsToDate}
          cellId={`group-${props.groupId}-actuals-to-date`}
          currencyCode={props.budget.currency}
        >
          {totalToDate}
        </Cell>
      )}
      {props.columnVisibility.monthlyActuals &&
        monthlyTotals.map((total, i) => (
          <Cell
            key={`group_header_monthly_total_${i}`}
            isDollarAmount
            bgColor="subheader"
            backingTransactions={monthlyTransactions[i]}
            cellId={`group-${props.groupId}-month-${i}`}
            currencyCode={props.budget.currency}
          >
            {total}
          </Cell>
        ))}
    </div>
  );
}

function GroupMemberRow(props: {
  category: FirestoreDocCategory;
  budget: BreadBudget;
  isScrolledX: boolean;
  columnVisibility: ColumnVisibility;
  convertedTransactions: BreadTransaction[];
}) {
  const transactionsForCategory = props.convertedTransactions.filter(
    (transaction) =>
      transaction.reviewed && transaction.categoryId === props.category.id,
  );

  function getTotalBudgeted() {
    const budgeted = props.budget.line_items.get(props.category.id);
    if (budgeted !== undefined) {
      return getSignedBudgetLineItemAmount(budgeted, props.category);
    }
    return 0;
  }

  function getBudgetedToDate() {
    return getTotalBudgeted() * getFractionOfTimeIntoBudget(props.budget);
  }

  const transactionsToDate: BreadTransaction[] = getTransactionsToDate(
    transactionsForCategory,
    props.budget,
  );
  const totalToDate = sumTransactions(transactionsToDate);
  const monthlyTransactions: BreadTransaction[][] = getMonthlyTransactions(
    transactionsForCategory,
    props.budget,
  );
  const monthlyTotals = getMonthlyTotals(monthlyTransactions);

  return (
    <div className={buildClasses("inline-flex", "min-w-full")}>
      <Cell width="lg" freezeIndex={0} align={"left"}>
        {/* Past a group's chevron and its gap, so categories line up under
            their group's name. */}
        <div
          className={buildClasses("w-full", "flex", "gap-2", "min-w-0", "pl-6")}
        >
          <span className={buildClasses("shrink-0")}>
            {props.category.emoji}
          </span>
          <span
            className={buildClasses(
              "overflow-hidden",
              "whitespace-nowrap",
              "text-ellipsis",
            )}
          >
            {props.category.name}
          </span>
        </div>
      </Cell>
      {props.columnVisibility.totalBudgeted && (
        <Cell
          width="md"
          freezeIndex={getFreezeIndex(props.columnVisibility, 1)}
          isDollarAmount
          showRightBorder={isRightmostFrozen(props.columnVisibility, 1)}
          cellId={`category-${props.category.id}-total-budgeted`}
          currencyCode={props.budget.currency}
        >
          {getTotalBudgeted()}
        </Cell>
      )}
      {props.columnVisibility.budgetedToDate && (
        <Cell
          width="md"
          freezeIndex={getFreezeIndex(props.columnVisibility, 2)}
          isDollarAmount
          showRightBorder={isRightmostFrozen(props.columnVisibility, 2)}
          cellId={`category-${props.category.id}-budgeted-to-date`}
          currencyCode={props.budget.currency}
        >
          {getBudgetedToDate()}
        </Cell>
      )}
      {props.columnVisibility.actualsToDate && (
        <Cell
          width="md"
          freezeIndex={getFreezeIndex(props.columnVisibility, 3)}
          isDollarAmount
          showRightShadow={shouldShowRightShadow(
            props.columnVisibility,
            3,
            props.isScrolledX,
          )}
          showRightBorder={isRightmostFrozen(props.columnVisibility, 3)}
          backingTransactions={transactionsToDate}
          cellId={`category-${props.category.id}-total-to-date`}
          currencyCode={props.budget.currency}
        >
          {totalToDate}
        </Cell>
      )}
      {props.columnVisibility.monthlyActuals &&
        monthlyTotals.map((total, i) => (
          <Cell
            key={`group_member_monthly_total_${i}`}
            isDollarAmount
            backingTransactions={monthlyTransactions[i]}
            cellId={`category-${props.category.id}-month-${i}`}
            currencyCode={props.budget.currency}
          >
            {total}
          </Cell>
        ))}
    </div>
  );
}

function UnreviewedRow(props: {
  budget: BreadBudget;
  isScrolledX: boolean;
  columnVisibility: ColumnVisibility;
  convertedTransactions: BreadTransaction[];
}): JSX.Element | null {
  const unreviewedTransactions = props.convertedTransactions.filter(
    (transaction) => !transaction.reviewed,
  );
  const transactionsToDate: BreadTransaction[] = getTransactionsToDate(
    unreviewedTransactions,
    props.budget,
  );
  const totalToDate = sumTransactions(transactionsToDate);
  const monthlyTransactions: BreadTransaction[][] = getMonthlyTransactions(
    unreviewedTransactions,
    props.budget,
  );
  const monthlyTotals = getMonthlyTotals(monthlyTransactions);

  if (totalToDate === 0) {
    return null;
  }

  return (
    <div
      className={buildClasses(
        "inline-flex",
        "min-w-full",
        "text-yellow-600",
        "border-b-tiny",
        "border-surface-600",
      )}
    >
      <Cell
        width="lg"
        freezeIndex={0}
        align={"left"}
        showRightBorder={isRightmostFrozen(props.columnVisibility, 0)}
      >
        Unreviewed
      </Cell>
      {props.columnVisibility.totalBudgeted && (
        <Cell
          width="md"
          freezeIndex={getFreezeIndex(props.columnVisibility, 1)}
          isDollarAmount
          showRightBorder={isRightmostFrozen(props.columnVisibility, 1)}
          cellId="unreviewed-total-budgeted"
          currencyCode={props.budget.currency}
        >
          {0}
        </Cell>
      )}
      {props.columnVisibility.budgetedToDate && (
        <Cell
          width="md"
          freezeIndex={getFreezeIndex(props.columnVisibility, 2)}
          isDollarAmount
          showRightBorder={isRightmostFrozen(props.columnVisibility, 2)}
          cellId="unreviewed-budgeted-to-date"
          currencyCode={props.budget.currency}
        >
          {0}
        </Cell>
      )}
      {props.columnVisibility.actualsToDate && (
        <Cell
          width="md"
          freezeIndex={getFreezeIndex(props.columnVisibility, 3)}
          isDollarAmount
          showRightShadow={shouldShowRightShadow(
            props.columnVisibility,
            3,
            props.isScrolledX,
          )}
          showRightBorder={isRightmostFrozen(props.columnVisibility, 3)}
          backingTransactions={transactionsToDate}
          cellId="unreviewed-total-to-date"
          currencyCode={props.budget.currency}
        >
          {totalToDate}
        </Cell>
      )}
      {props.columnVisibility.monthlyActuals &&
        monthlyTotals.map((total, i) => (
          <Cell
            key={i}
            isDollarAmount
            backingTransactions={monthlyTransactions[i]}
            cellId={`unreviewed-month-${i}`}
            currencyCode={props.budget.currency}
          >
            {total}
          </Cell>
        ))}
    </div>
  );
}

function Cell({
  width = "sm",
  freezeIndex = null,
  isDollarAmount = false,
  align = "right",
  bgColor = "normal",
  showRightShadow = false,
  showRightBorder = false,
  cellId = undefined,
  backingTransactions = undefined,
  currencyCode = undefined,
  children,
}: {
  width?: "sm" | "md" | "lg";
  freezeIndex?: number | null;
  isDollarAmount?: true | false;
  align?: "left" | "right";
  bgColor?: "normal" | "header" | "subheader" | "warning";
  showRightShadow?: boolean;
  showRightBorder?: boolean;
  cellId?: string | undefined;
  backingTransactions?: BreadTransaction[] | undefined;
  currencyCode?: string;
  children: React.ReactNode;
}) {
  const [selectedCellId, setSelectedCellId] = useContext(CellSelectionContext);
  const [, setSelectedTransactions] = useContext(SelectedTransactionsContext);

  const isSelected = selectedCellId === cellId;

  function isSelectable() {
    return cellId !== undefined && backingTransactions !== undefined;
  }

  const handleClick = () => {
    if (!isSelectable()) {
      return;
    }
    if (!isSelected) {
      setSelectedCellId(cellId!);
      setSelectedTransactions(backingTransactions!);
    } else {
      setSelectedCellId(null);
      setSelectedTransactions(null);
    }
  };

  return (
    <div
      className={buildClasses(
        {
          switch: width,
          cases: new Map([
            ["sm", buildClasses("w-32")],
            ["md", buildClasses("w-40")],
            ["lg", buildClasses("w-60")],
          ]),
        },
        {
          if: freezeIndex !== null,
          then: buildClasses("sticky", "border-surface-600"),
        },
        {
          if: freezeIndex === 0,
          then: buildClasses("left-0"),
          elseIfs: [
            {
              elseIf: freezeIndex === 1,
              then: buildClasses("left-60"),
            },
            {
              elseIf: freezeIndex === 2,
              then: buildClasses("left-[25rem]"),
            },
            {
              elseIf: freezeIndex === 3,
              then: buildClasses("left-[35rem]"),
            },
            {
              elseIf: freezeIndex === 4,
              then: buildClasses("left-[45rem]"),
            },
          ],
        },
        {
          switch: bgColor,
          cases: new Map([
            ["normal", "bg-surface-50"],
            ["header", "bg-surface-50"],
            ["subheader", "bg-surface-300"],
            ["warning", "bg-yellow-200"],
          ]),
        },
        { if: showRightShadow, then: "clipped-shadow-right" },
        {
          if: showRightBorder,
          then: buildClasses("border-r", "border-surface-600"),
        },
        "flex",
        "items-center",
        "flex-shrink-0",
        "flex-grow",
        "px-2",
        "z-100",
        "overflow-hidden",
        "whitespace-nowrap",
      )}
      style={{ height: `${CELL_HEIGHT}rem` }}
      onClick={handleClick}
    >
      <div
        className={buildClasses(
          {
            switch: align,
            cases: new Map([
              ["left", "justify-start"],
              ["right", "justify-end"],
            ]),
          },
          {
            if: isSelectable() && !isSelected,
            then: buildClasses(
              "cursor-pointer",
              "hover:bg-purple-50",
              "hover:text-purple-600",
            ),
          },
          {
            if: isSelected,
            then: buildClasses("bg-purple-200", "text-purple-600"),
          },
          "w-full",
          "flex",
          "items-center",
          "px-2",
          "py-1",
          "rounded-md",
          "group-cell",
        )}
      >
        {isSelected && (
          <RyeButton
            icon="close"
            size="xs"
            vibe="primary"
            variant="transparent"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCellId(null);
              setSelectedTransactions(null);
            }}
          />
        )}
        {isDollarAmount && typeof children === "number" ? (
          <DollarAmount
            n={children}
            showDollarSign
            showPlus={children > 0 && bgColor === "warning"}
            align={align}
            size="sm"
            currencyCode={currencyCode}
          />
        ) : (
          children
        )}
      </div>
    </div>
  );
}
